import BezierEasing from 'bezier-easing';
import React from 'react';
import styled from 'styled-components';
import { Text } from '@oneaudi/unified-web-components';
import type { TrackingClickEvent } from '../../utils/tracking';
import { useTracking } from '../../utils/tracking';
import { TrackingContext } from '../../context';
import { breakpointM } from '../footer/audi-footer';

type ToTopButtonProps = {
  label?: string;
};

export const timingXXL = 660;
export const averagePageHeight = 4000;
export const easeOut = BezierEasing(0.21, 0.55, 0.43, 1);

const ToTopStyledLink = styled.a<{
  href: string;
  onClick: (event: React.MouseEvent) => void;
  children?: React.ReactNode;
}>`
  color: var(--one-footer-color-white);
  align-items: center;
  display: inline-flex;

  & > span {
    color: var(--one-footer-color-white);
    margin-right: var(--one-footer-space-s);
  }
  & > svg {
    transform: rotate(180deg);
  }
`;

const ToTopLink = React.forwardRef<HTMLAnchorElement, ToTopButtonProps>(({ label }, ref) => {
  const trackingService = React.useContext(TrackingContext);
  const { sendClickEvent } = useTracking(trackingService);
  const isMobile = typeof window !== 'undefined' && window && window.innerWidth < breakpointM;

  const sendButtonClickEvent = (
    Buttonlabel: string,
    url: string,
    variant: string,
    openin: string,
    position: string,
  ) => {
    const event: TrackingClickEvent = {
      linkLabel: Buttonlabel,
      linkUrl: url,
      elementName: variant,
      headline: isMobile ? 'mobile view' : 'desktop view',
      OpenIn: openin,
      position,
    };
    sendClickEvent(event);
  };

  const onClick = (event: React.MouseEvent) => {
    event.preventDefault();
    //@ts-expect-error label is an optional argument
    sendButtonClickEvent(label, '', 'text link', 'toTop', '');
    animate();
  };

  return (
    <ToTopStyledLink aria-label={label} href="#" onClick={onClick} ref={ref}>
      <Text
        tag="span"
        variant={{
          type: 'copy',
          order: '2',
          style: 'normal',
        }}
      >
        {label}
      </Text>

      <svg height="24" width="24" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 10l5.5 5.5L18 10" fill="none" stroke="currentColor" />
      </svg>
    </ToTopStyledLink>
  );
});

ToTopLink.displayName = 'ToTopLink';

export function animate(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const body = document.querySelector('body');
  const bodyHeight = body?.scrollHeight;
  const duration = bodyHeight ? (bodyHeight * timingXXL) / averagePageHeight : undefined;
  const startPosition = window.scrollY;
  const startTime: number = Date.now();

  function render(): void {
    const timeNormalized: number | undefined = duration
      ? (Date.now() - startTime) / duration
      : undefined;
    const easeOutTime = timeNormalized ? easeOut(timeNormalized) : 0;
    window.scrollTo(0, startPosition - startPosition * easeOutTime);
    if (timeNormalized && timeNormalized <= 1) {
      requestAnimationFrame(render);
    }
  }

  setTimeout(render, 1);
}

export default ToTopLink;
